import crypto from "crypto";
import axios from "axios";
import CryptoJS from "crypto-js";
import logger from "@/utils/logger";
import { AppRuntimeConfig } from "@/types/AppRuntimeEnv";
// @ts-ignore
import NodeRSA from "node-rsa";
import { IPermissions, AuthorResourceEnum, IClientConfig } from "@/types/common";
import config, { ChatConfig, ConfigEnum } from "@/config/config";
import clientConfig, { ClientConfigEnum } from "@/config/clientConfig";
import { changeSingleDomain } from "./tools";
import { getWebEnvObj } from "@/utils";
import duccConfig from "../ducc.js";

const log = logger.getLogger("EnvConfig");

const sKey = {
  pubkey:
    "-----BEGIN PUBLIC KEY-----\n" +
    "MIGfMA0GCSqGSIb3DQEBAQUAA4GNADCBiQKBgQCn8CHZxb1rcxKeCv5X5DzU9Jgg\n" +
    "wNa/1rn5NiKECj6zM4JA0v9SjH6eNjw2s0kVVd7wFsMp2MMODMMD5R55W4y9EsTi\n" +
    "wWtRR9t8ihpYHbzZJRCLX4NpLMpdEypcr3tWuSev+xJ2R/JQUbykg1u2IeFyf4hE\n" +
    "DqfNcuORRduys4NMbwIDAQAB\n" +
    "-----END PUBLIC KEY-----"
};
const encrypt = (rKey: any) => {
  // const publicKey = pki.publicKeyFromPem(sKey.pubkey);
  // return publicKey.encrypt(rKey, 'RAW', {
  //     md: md.sha256.create(),
  //     mgf1: {
  //         md: md.sha1.create(),
  //     },
  // });
  const key = new NodeRSA(sKey.pubkey, "pkcs8-public", {
    encryptionScheme: "pkcs1"
  });
  return key.encrypt(rKey, "base64");
};

function getRuntimeAppId() {
  let key = "";
  let appId = "com.jd.ee.me.client";
  key =
    (window as any)?._BTS_CONFIG?.REACT_APP_RUNTIME || process.env.REACT_APP_RUNTIME || "mebjJZB";
  appId = `${appId}.${key}`;
  return appId;
}

function getSsoAppkey() {
  const duccHost = location.host || "";
  return duccHost.includes("egov") ? "jzbDesktop" : "meDesktop";
}

function genRandomKey() {
  console.log("genRandomKeygenRandomKey", process.env.REACT_APP_CIPHERTEXT);
  return process.env.REACT_APP_CIPHERTEXT || crypto.randomBytes(16).toString("hex").slice(0, 16);
}

function genRequestUrl(isBeta: boolean, duccHost?: string) {
  if (duccHost) {
    return new URL(`https://${duccHost}/common/api/sso/domains`);
  }
  return new URL(`https://jzb${isBeta ? "-pre" : ""}.jdcloud.com/common/api/sso/domains`);
}

function changeDuccSingleDomain(data: any) {
  const app_runtime =
    (window as any)?._BTS_CONFIG?.REACT_APP_RUNTIME || process.env.REACT_APP_RUNTIME;
  const not_single = process.env.REACT_APP_NOT_SINGLE === "true";
  if (not_single || (app_runtime !== "mebjJZB" && app_runtime !== undefined)) {
    return data;
  }
  if (typeof data === "string") {
    return changeSingleDomain(data, "");
  }
  let newObj: any = Array.isArray(data) ? [] : {};
  for (let item in data) {
    if (typeof data[item] === "object") {
      newObj[item] = changeDuccSingleDomain(data[item]);
    } else {
      if (/\.e?gov\.cn|\.jdcloud\.com/.test(data[item])) {
        let containsHttps = data[item].indexOf("https") > -1;
        newObj[item] = changeSingleDomain(containsHttps ? data[item] : `https://${data[item]}`, "")
          .replace(containsHttps ? "" : "https://", "")
          .replace(/\/apijoyspace\b/, "/joyspace-api")
          .replace(/\/otjoyspace\b/, "/joyspace-ot")
          .replace(/\/qr\b/, "/qr-code");
      } else {
        newObj[item] = data[item];
      }
    }
  }
  return newObj;
}

export async function getRuntimeConfig(isBeta: boolean): Promise<AppRuntimeConfig[]> {
  try {
    let runtimeConfig: any;
    if (process.env.REACT_APP_DUCC === "local") {
      runtimeConfig = duccConfig;
      return runtimeConfig;
    } else {
      const appId = getRuntimeAppId();
      const randomKey = genRandomKey();
      const reqUrl = genRequestUrl(isBeta, getWebEnvObj().REACT_APP_DUCC_HOST);
      reqUrl.searchParams.append("content", appId);
      const headers = {
        "sso-app-key": getSsoAppkey(),
        cipherText: encrypt(randomKey)
      };
      logger.debug("get app config from ducc123: ", {
        url: reqUrl.toString(),
        headers,
        isBeta
      });
      const res = await axios.get(reqUrl.toString(), {
        headers,
        timeout: 3000
      });
      const { errorCode, errorMsg, content } = res.data;
      if (errorCode !== "0") {
        throw new Error(`response error: [${errorCode}]${errorMsg}`);
      }
      const key = CryptoJS.enc.Utf8.parse(randomKey);
      const iv = CryptoJS.enc.Utf8.parse("0102030405060708");
      const decrypted = CryptoJS.AES.decrypt(content, key, {
        iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7
      });
      const str = CryptoJS.enc.Utf8.stringify(decrypted);
      const oldData = JSON.parse(str);
      const data = changeDuccSingleDomain(oldData);
      console.log("data---", data);
      return data;
    }
    // return data.find((item: any) => item.config.appId === appId);
  } catch (error) {
    logger.error(`failed to get app config from ducc: ${error}`);
    // eslint-disable-next-line global-require
    throw new Error(`failed to get app config from ducc: ${error}`);
  }
}

export async function getEnvByRuntimeAndEnv(
  runtime: string,
  env: string,
  isDev?: boolean
): Promise<AppRuntimeConfig> {
  log.debug(`get config by: runtime: ${runtime}, env: ${env}`);
  try {
    if (isDev) {
      // eslint-disable-next-line no-param-reassign
      env = "beta";
    }
    const runtimeAppId = getRuntimeAppId() || `com.jd.ee.me.client.${runtime}`;
    let runtimeConfig: any;
    if (process.env.REACT_APP_DUCC === "local") {
      runtimeConfig =
        Array.isArray(duccConfig) &&
        duccConfig.find((item: any) => item.config.appId === runtimeAppId);
    } else {
      // const runtimeAppId = `com.jd.ee.me.client.${runtime}`;
      const randomKey = process.env.REACT_APP_CIPHERTEXT || crypto.randomBytes(16).toString("hex").slice(0, 16);

      const reqUrl = genRequestUrl(env === "beta", getWebEnvObj().REACT_APP_DUCC_HOST);
      reqUrl.searchParams.append("content", runtimeAppId);
      const headers = {
        "sso-app-key": "meDesktop",
        cipherText: encrypt(randomKey)
      };
      logger.debug("get app config from ducc: ", { url: reqUrl.toString(), headers });
      const res = await axios.get(reqUrl.toString(), {
        headers,
        timeout: 15000
      });
      console.log("duccduccduccduccres", res);
      if (res.status !== 200) {
        throw new Error(
          `failed to request config(${runtimeAppId}) server [${res.status}]${res.statusText}`
        );
      }
      const { errorCode, errorMsg, content } = res.data;
      if (errorCode !== "0") {
        throw new Error(`response error: [${errorCode}]${errorMsg}`);
      }
      const key = CryptoJS.enc.Utf8.parse(randomKey);
      const iv = CryptoJS.enc.Utf8.parse("0102030405060708");
      const decrypted = CryptoJS.AES.decrypt(content, key, {
        iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7
      });
      const str = CryptoJS.enc.Utf8.stringify(decrypted);
      // console.log("ducc-str", str);
      const oldData = JSON.parse(str);
      const data = changeDuccSingleDomain(oldData);
      console.log("getEnvByRuntimeAndEnv-data", data);
      // log.debug('ducc json -> \n', JSON.stringify(data, null, '\t'));
      runtimeConfig = data.find((item: any) => item.config.appId === runtimeAppId);
      console.log("runtimeConfig-data", runtimeConfig);
    }
    if (
      !runtimeConfig ||
      !runtimeConfig.config ||
      !Array.isArray(runtimeConfig.config.environments)
    ) {
      throw new Error(`can not get runtimeConfig for ${runtimeAppId}`);
    }
    console.log("runtimeConfig----", runtimeConfig);
    return runtimeConfig;
  } catch (error) {
    logger.error(`failed to get app config from ducc: ${error}`);
    // eslint-disable-next-line global-require
    throw new Error(`failed to get app config from ducc: ${error}`);
  }
}

export function ApplyAuthorResourceToClientConfig(clientConfigPermission: IClientConfig) {
  clientConfig[ClientConfigEnum.JD_AUDIO] = verifyEnableInClientConfig(
    clientConfigPermission,
    ClientConfigEnum.JD_AUDIO
  );
}

export function verifyEnableInClientConfig(
  clientConfigPermission: IClientConfig,
  key: ClientConfigEnum
) {
  return clientConfigPermission[key] === 1;
}

export function ApplyAuthorResourceToConfig(permissions: IPermissions[]) {
  // voip
  config[ChatConfig.CHAT_SESSION_CREATE_VOIP_ENABLE] = findResourceIdInAuthorPermission(
    permissions,
    AuthorResourceEnum.VOIP
  );
  // 加入会议
  config[ConfigEnum.TOP_BAR_MENU_MEETING_JOIN] = findResourceIdInAuthorPermission(
    permissions,
    AuthorResourceEnum.MEETING_JOIN
  );

  // 我的会议
  config[ConfigEnum.TOP_BAR_MENU_MEETING_MEETINGLIST] = findResourceIdInAuthorPermission(
    permissions,
    AuthorResourceEnum.MEETING_JOIN
  );
  // 创建会议
  config[ConfigEnum.TOP_BAR_MENU_MEETING_CREATE] = findResourceIdInAuthorPermission(
    permissions,
    AuthorResourceEnum.MEETING
  );
  // 预约会议
  config[ConfigEnum.TOP_BAR_MENU_MEETING_APPOINT] = findResourceIdInAuthorPermission(
    permissions,
    AuthorResourceEnum.MEETING
  );
  // 直播会议
  config[ConfigEnum.TOP_BAR_MENU_MEETING_LIVE] = findResourceIdInAuthorPermission(
    permissions,
    AuthorResourceEnum.CREAT_LIVE_STREAM
  );

  config[ConfigEnum.EXTERNAL_CONTACTS_ORG] = findResourceIdInAuthorPermission(
    permissions,
    AuthorResourceEnum.ORG
  );
  config[ConfigEnum.EXTERNAL_CONTACTS_MY_DEPARTMENT] = findResourceIdInAuthorPermission(
    permissions,
    AuthorResourceEnum.MY_DEPARTMENT
  );
  // 搜索外部联系人控制部门
  config[ConfigEnum.EXTERNAL_CONTACTS_SEARCH_DEPARTMENT] = findResourceIdInAuthorPermission(
    permissions,
    AuthorResourceEnum.SEARCH_DEPARTMENT
  );
  config[ConfigEnum.EXTERNAL_CONTACTS_ADD_CONTACTS] = findResourceIdInAuthorPermission(
    permissions,
    AuthorResourceEnum.ADD_CONTACTS
  );
  config[ConfigEnum.EXTERNAL_CONTACTS_CREAT_GROUP] = findResourceIdInAuthorPermission(
    permissions,
    AuthorResourceEnum.CREAT_GROUP
  );
  config[ConfigEnum.EXTERNAL_CONTACTS_CREAT_SCHEDULE] = findResourceIdInAuthorPermission(
    permissions,
    AuthorResourceEnum.CREAT_SCHREDULE
  );
  config[ConfigEnum.EXTERNAL_CONTACTS_CREAT_TASK] = findResourceIdInAuthorPermission(
    permissions,
    AuthorResourceEnum.CREAT_TASK
  );
  config[ConfigEnum.EXTERNAL_CONTACTS_CREAT_URGING] = findResourceIdInAuthorPermission(
    permissions,
    AuthorResourceEnum.CREAT_URGING
  );
  config[ConfigEnum.EXTERNAL_CONTACTS_CREAT_DOCUMENT] = findResourceIdInAuthorPermission(
    permissions,
    AuthorResourceEnum.CREAT_DOCUMENT
  );
  config[ConfigEnum.EXTERNAL_CONTACTS_EXPORT_DOCUMENT] = findResourceIdInAuthorPermission(
    permissions,
    AuthorResourceEnum.EXPORT_DOCUMENT
  );
  // jdVideo
  config[ConfigEnum.CHAT_SESSION_CREATE_JD_VIDEO_ENABLE] = findResourceIdInAuthorPermission(
    permissions,
    AuthorResourceEnum.JD_VIDEO
  );
  // 会议权限遍历
  [
    ConfigEnum.AUDIOVIDEO_CREATEMINUTES,
    ConfigEnum.AUDIOVIDEO_EXPORTPARTICIPANTS,
    ConfigEnum.AUDIOVIDEO_EXPORTMEETINGSIGNINS
  ].forEach((i: ConfigEnum) => {
    // @ts-ignore
    config[i] = findResourceIdInAuthorPermission(permissions, i);
  });

  // eventTracking.startUploading
  config[ConfigEnum.EVENTTRACKING_START_UPLOAD_ENABLE] = findResourceIdInAuthorPermission(
    permissions,
    AuthorResourceEnum.EVENTTRACKING_START_UPLOAD
  );
}

export function findResourceIdInAuthorPermission(
  permissions: IPermissions[],
  code: AuthorResourceEnum
) {
  // const index = permissions.findIndex((permission) => {
  //     return permission.resources.findIndex((r) => r.resourceId === resourceId) !== -1;
  // });
  const index = permissions.findIndex((permission) => permission.code === code);
  // console.log(index, permissions, code, 'index');
  return index !== -1;
}
